import { Plus, Trash2, Search, LayoutGrid, List as ListIcon, Filter, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import type { Product } from '../../hooks/useProductSystem';
import { useProductSystem } from '../../hooks/useProductSystem';
import { ProductCard } from './ProductCard';
import { ProductDetails } from './ProductDetails';
import { ProductForm } from './ProductForm';

export function ProductList() {
  const { chatbotPublicId } = useChatbot();
  const { products, categories, deleteProduct, deleteAllProducts, reorderProducts, loading, error, initialize, initialized } = useProductSystem();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Nouvelles variables d'état pour la recherche et l'affichage
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initialize(chatbotPublicId);
    }
  }, [chatbotPublicId, initialize, initialized]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || !chatbotPublicId) return;

    const newProducts = [...products];
    const draggedProduct = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(index, 0, draggedProduct);

    await reorderProducts(chatbotPublicId, newProducts.map(p => p.id));
    setDraggedIndex(index);
  };

  const handleDelete = async (productId: string) => {
    if (!chatbotPublicId) return;
    await deleteProduct(chatbotPublicId, productId);
  };

  const handleDeleteAll = async () => {
    if (!chatbotPublicId) return;
    if (confirmDelete) {
      await deleteAllProducts(chatbotPublicId);
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
      // Réinitialiser l'état de confirmation après 5 secondes si l'utilisateur ne confirme pas
      setTimeout(() => setConfirmDelete(false), 5000);
    }
  };

  // Filtrer les produits selon la recherche et la catégorie
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === '' || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === null || product.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Obtenir la liste des catégories uniques dans les produits
  const productCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
  
  // Calculer le nombre de colonnes en fonction du nombre de produits
  const getGridCols = () => {
    if (viewMode === 'list') return 'grid-cols-1';
    if (filteredProducts.length === 1) return 'grid-cols-1';
    if (filteredProducts.length === 2) return 'grid-cols-1 sm:grid-cols-2';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  };

  if (loading && !products.length) {
    return <p className="text-center py-4">Loading products...</p>;
  }

  if (error) {
    return <p className="text-center py-4 text-red-500">Error: {error}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => setShowAddForm(true)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors mr-2"
        >
          <Plus className="w-4 h-4" />
          Add a product manually
        </button>

        {products.length > 0 && (
          <button
            onClick={handleDeleteAll}
            className={`flex items-center justify-center gap-2 px-4 py-3 ${
              confirmDelete 
                ? 'bg-red-600 text-white' 
                : 'bg-red-500/20 text-red-400'
            } rounded-lg hover:bg-red-500/30 transition-colors`}
          >
            <Trash2 className="w-4 h-4" />
            {confirmDelete ? 'Confirm Delete All' : 'Delete All'}
          </button>
        )}
      </div>

      {products.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {/* Barre de recherche */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {searchTerm && (
              <button 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setSearchTerm('')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Filtre par catégorie */}
          {productCategories.length > 0 && (
            <div className="relative">
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value || null)}
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
              >
                <option value="">All categories</option>
                {productCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          )}
          
          {/* Toggle de vue */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center justify-center p-2 ${viewMode === 'grid' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-500'}`}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center justify-center p-2 ${viewMode === 'list' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-500'}`}
            >
              <ListIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {showAddForm && (
        <ProductForm onClose={() => setShowAddForm(false)} />
      )}

      {editingProduct && (
        <ProductForm 
          product={editingProduct} 
          onClose={() => setEditingProduct(null)} 
        />
      )}

      {filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400">
            {products.length === 0 
              ? 'No products' 
              : 'No products match your search'}
          </p>
        </div>
      ) : (
        <div className={`grid ${getGridCols()} gap-4`}>
          {filteredProducts.map((product, index) => (
            <div
              key={product.id}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={() => setDraggedIndex(null)}
              className={viewMode === 'list' ? 'col-span-full' : ''}
            >
              <ProductCard
                product={product}
                onDelete={() => handleDelete(product.id)}
                onDragStart={() => handleDragStart(index)}
                onShowDetails={() => setSelectedProduct(product)}
                onEdit={() => setEditingProduct(product)}
                viewMode={viewMode}
              />
            </div>
          ))}
        </div>
      )}

      {selectedProduct && (
        <ProductDetails
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}